import { createContext, useCallback, useContext, useEffect, useState } from "react";
import {
    authApi, clientApi,
    getAdminToken, setAdminToken, clearAdminToken,
    getClientToken, setClientToken, clearClientToken,
} from "@/api/client";

const AuthContext = createContext({
    // Admin
    user: null,
    loading: true,
    login: async () => {},
    logout: () => {},
    // Client
    client: null,
    clientLoading: true,
    clientLogin: async () => {},
    clientRegister: async () => {},
    clientLogout: () => {},
});

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const [client, setClient] = useState(null);
    const [clientLoading, setClientLoading] = useState(true);

    // Verify admin token
    const verifyAdmin = useCallback(async () => {
        if (!getAdminToken()) { setUser(null); setLoading(false); return; }
        try {
            const { data } = await authApi.get("/admin/me");
            setUser(data);
        } catch { clearAdminToken(); setUser(null); }
        finally { setLoading(false); }
    }, []);

    // Verify client token
    const verifyClient = useCallback(async () => {
        if (!getClientToken()) { setClient(null); setClientLoading(false); return; }
        try {
            const { data } = await clientApi.get("/client/me");
            setClient(data);
        } catch { clearClientToken(); setClient(null); }
        finally { setClientLoading(false); }
    }, []);

    useEffect(() => {
        verifyAdmin();
        verifyClient();
    }, [verifyAdmin, verifyClient]);

    // --- Admin ---
    const login = async (email, password) => {
        const { data } = await authApi.post("/admin/login", { email, password });
        // 2FA challenge flow
        if (data.requires_2fa) return { requires2fa: true, challenge: data.challenge };
        setAdminToken(data.token);
        setUser(data.user);
        return data.user;
    };
    const loginVerify2fa = async (challenge, code) => {
        const { data } = await authApi.post("/admin/login/2fa", { challenge, code });
        setAdminToken(data.token);
        setUser(data.user);
        return data.user;
    };
    const refreshAdmin = async () => {
        try { const { data } = await authApi.get("/admin/me"); setUser(data); } catch (err) { console.warn("refreshAdmin failed:", err?.message); }
    };
    const logout = () => {
        clearAdminToken();
        setUser(null);
        if (typeof window !== "undefined") window.location.href = "/admin/login";
    };

    // --- Client ---
    const clientLogin = async (email, password) => {
        const { data } = await clientApi.post("/client/login", { email, password });
        setClientToken(data.token);
        setClient(data.user);
        return data.user;
    };
    const clientRegister = async (payload) => {
        const { data } = await clientApi.post("/client/register", payload);
        setClientToken(data.token);
        setClient(data.user);
        return data.user;
    };
    const clientLogout = () => {
        clearClientToken();
        setClient(null);
        if (typeof window !== "undefined") window.location.href = "/";
    };

    return (
        <AuthContext.Provider
            value={{
                user, loading, login, loginVerify2fa, refreshAdmin, logout,
                client, clientLoading, clientLogin, clientRegister, clientLogout,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}
